'use client'

import { useState, useEffect } from 'react'
import { createClient } from '~/lib/supabase/client'
import Image from 'next/image'
import Link from 'next/link'
import { MapPin, Sparkles, Users, Eye, Compass } from 'lucide-react'
import PathCollectiveLogo from '~/components/PathCollectiveLogo'
import { Button } from '~/components/ui/button'
import { LoadError, InlineError } from '~/components/ui/LoadError'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip'
import { env } from '~/env'
import { resolveImageUrl, defaultEventImage } from '~/lib/images'
import { summarizeLocation } from '~/lib/location'
import { formatEventDate } from '~/lib/event-dates'
import EventSaveButton from '~/components/events/EventSaveButton'
import EventCalendarButton from '~/components/events/EventCalendarButton'
import EventShareButton from '~/components/events/EventShareButton'
import { AuthPromptDialog } from '~/components/auth/AuthPromptDialog'
import { FitBadge, bucketFromScore, type FitBucket } from '~/components/matching/fit-badge'
import {
  MatchScopeToggle,
  type MatchScope,
  type ResolvedMatchScope,
} from '~/components/matching/match-scope-toggle'
import type { Recommendation, RecommendationEvent } from '~/components/matching/recommendation'

function getFirstImageUrl(rawUrl: string, categories?: { name: string }[] | null): string {
  const url = rawUrl?.includes(',') ? rawUrl.split(',')[0].trim() : rawUrl
  return url ? resolveImageUrl(url) : defaultEventImage(categories)
}

function EventCard({
  event,
  reasoning,
  relevanceScore,
  fitBucket,
  imageUrl,
  savedEventIds,
  onSavedToggle,
}: {
  event: any
  reasoning?: string
  relevanceScore?: number
  fitBucket?: FitBucket | null
  imageUrl?: string
  savedEventIds: string[]
  onSavedToggle: (eventId: string, saved: boolean) => void
}) {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const finalImageUrl = getFirstImageUrl(imageUrl || event?.image_url || '', event?.categories)

  return (
    <div
      data-testid="event-card"
      className="relative flex-shrink-0 w-[280px] sm:w-[360px] lg:w-[420px] bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col"
    >
      <Link href={`/events/${event.id}`} className="group block flex-1">
        <div className="relative h-48 w-full rounded-t-xl overflow-hidden">
          <Image
            src={finalImageUrl}
            alt={event.title || 'Event'}
            fill
            className="object-cover transition-transform group-hover:scale-[1.02]"
            sizes="420px"
            quality={85}
            onError={(e) => {
              const target = e.target as HTMLImageElement
              const fallback = defaultEventImage(event?.categories)
              if (!target.src.endsWith(fallback)) {
                target.src = fallback
              }
            }}
          />
          {/* Fit bucket — deterministic score bucketed (see fit-badge.tsx).
              Falls back to deriving from the raw score for legacy rows. */}
          {(fitBucket ?? bucketFromScore(relevanceScore)) && (
            <div className="absolute top-2 left-2 z-10">
              <FitBadge fitBucket={fitBucket ?? bucketFromScore(relevanceScore)} />
            </div>
          )}
        </div>

        <div className="p-4 flex-1 flex flex-col">
          <h4 className="text-base font-semibold text-[#2C2D2B] line-clamp-2 group-hover:text-[#DE866D] transition-colors mb-1">
            {event.title || 'Untitled Event'}
          </h4>
          <div className="text-sm text-gray-600 mb-2">{formatEventDate(event)}</div>
          <div className="hidden sm:flex flex-wrap gap-1.5 mb-2">
            {event.subcategories?.map((s: any) => (
              <span key={s.id} className="px-2 py-0.5 text-xs rounded-full bg-[#EEE] text-[#444]">
                {s.name}
              </span>
            ))}
          </div>
          <div className="flex items-center text-sm text-gray-600 mt-1 mb-2">
            <MapPin className="h-3.5 w-3.5 mr-1" /> {summarizeLocation(event.location)}
          </div>
          {reasoning && (
            <div className="hidden sm:block mt-auto p-3 bg-gray-100 rounded-lg">
              <h5 className="text-xs font-semibold text-gray-800 mb-1">WHY THIS MATCHES YOU</h5>
              <p className="text-sm text-gray-600 leading-relaxed">{reasoning}</p>
            </div>
          )}
        </div>
      </Link>

      {/* Buttons moved outside Link and outside overflow-hidden to fix UI issue. Follows same pattern as EventsDiscoverClient component */}
      <div className="absolute top-2 right-2 flex gap-1 z-10">
        <div onClick={(e) => e.preventDefault()} className="bg-white/90 backdrop-blur-sm rounded-full p-1 shadow-sm">
          <EventSaveButton eventId={event.id} onToggle={onSavedToggle} save={savedEventIds.includes(event.id)} />
        </div>
        <div onClick={(e) => e.preventDefault()} className="bg-white/90 backdrop-blur-sm rounded-full p-1 shadow-sm">
          <EventCalendarButton
            title={event.title}
            startTime={event.start_datetime || event.date}
            endTime={event.end_datetime || event.start_datetime || event.date}
            location={event.location}
            description={event.description}
            timezone={event.timezone}
          />
        </div>
        <div onClick={(e) => e.preventDefault()} className="bg-white/90 backdrop-blur-sm rounded-full p-1 shadow-sm">
          <EventShareButton eventUrl={`${origin}/events/${event.id}`} />
        </div>
      </div>
    </div>
  )
}

const SECTION_VARIANTS = {
  teal: {
    band: 'bg-[#DE866D]/5',
    badge: 'bg-[#DE866D]/15',
    icon: 'text-[#DE866D]',
    count: 'bg-[#DE866D]/10 text-[#DE866D]',
  },
  coral: {
    band: 'bg-[#E9A88B]/5',
    badge: 'bg-[#E9A88B]/20',
    icon: 'text-[#E9A88B]',
    count: 'bg-[#E9A88B]/15 text-[#AC5E47]',
  },
  indigo: {
    band: 'bg-indigo-50/70',
    badge: 'bg-indigo-100',
    icon: 'text-indigo-500',
    count: 'bg-indigo-100 text-indigo-600',
  },
  mint: {
    band: 'bg-[#75A096]/5',
    badge: 'bg-[#75A096]/15',
    icon: 'text-[#75A096]',
    count: 'bg-[#75A096]/10 text-[#4E7A6F]',
  },
} as const

type SectionVariant = keyof typeof SECTION_VARIANTS

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
  count,
  actions,
  variant = 'teal',
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  subtitle: string
  count?: number
  actions?: React.ReactNode
  variant?: SectionVariant
}) {
  const v = SECTION_VARIANTS[variant]
  return (
    <div className={`relative flex flex-col sm:flex-row sm:justify-between px-5 py-4 border-b border-gray-100 ${v.band}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${v.badge}`}>
          <Icon className={`h-5 w-5 ${v.icon}`} />
        </div>
        <div>
          <h3 className="text-base font-semibold text-[#2C2D2B] leading-tight">{title}</h3>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {count !== undefined && count > 0 && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${v.count}
      absolute right-5 bottom-1 -translate-y-1/2
      sm:static sm:translate-y-0`}>
            {count}
          </span>
        )}
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  )
}

type MatchSection = 'recs' | 'followed' | 'interest' | 'comfortZone'

function EmptyState({ message, cta }: { message: string; cta?: { label: string; href: string } }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
      <p className="text-sm text-gray-500">{message}</p>
      {cta && (
        <Link href={cta.href} className="text-sm text-[#DE866D] font-medium hover:underline mt-2 inline-block">
          {cta.label}
        </Link>
      )}
    </div>
  )
}

export default function AfterOnboardingPage() {
  const supabase = createClient()
  const [user, setUser] = useState<any>(null)
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [followedEvents, setFollowedEvents] = useState<any[]>([])
  const [interestEvents, setInterestEvents] = useState<any[]>([])
  const [comfortZoneEvents, setComfortZoneEvents] = useState<any[]>([])
  const [savedEventIds, setSavedEventIds] = useState<string[]>([])
  const [accessToken, setAccessToken] = useState<string | null>(null)
  // #1409 — the server's own gate on /events/recommendations (412 Precondition
  // Failed when onboarding isn't COMPLETE), so a finish-onboarding prompt can
  // replace the recommendations rather than the generic available-events
  // fallback quietly standing in for them.
  const [onboardingIncomplete, setOnboardingIncomplete] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [showMatchAuthPrompt, setShowMatchAuthPrompt] = useState(false)
  // Per-section loading states for progressive rendering
  const [loadingRecs, setLoadingRecs] = useState(true)
  const [loadingFollowed, setLoadingFollowed] = useState(true)
  const [loadingInterest, setLoadingInterest] = useState(true)
  const [loadingComfortZone, setLoadingComfortZone] = useState(true)
  const [initialLoading, setInitialLoading] = useState(true)
  // #997 — Local/Everywhere. Null until the API has resolved it; the toggle
  // renders only once we know whether the member has a state to be local to.
  const [matchScope, setMatchScope] = useState<ResolvedMatchScope | null>(null)
  const [matchScopePending, setMatchScopePending] = useState(false)
  // Bumped when the scope changes, to re-run the section fetches against the
  // new filter. Every section is filtered server-side, so a refetch is all
  // that is needed.
  const [sectionsReloadKey, setSectionsReloadKey] = useState(0)
  // #1179 — a section whose fetch failed says so (with Retry) instead of
  // rendering its empty state.
  const [failedSections, setFailedSections] = useState<Record<MatchSection, boolean>>({
    recs: false, followed: false, interest: false, comfortZone: false,
  })
  const [actionError, setActionError] = useState<string | null>(null)
  const markSection = (section: MatchSection, failed: boolean) =>
    setFailedSections((prev) => ({ ...prev, [section]: failed }))
  const reloadSections = () => {
    setLoadingRecs(true)
    setLoadingFollowed(true)
    setLoadingInterest(true)
    setLoadingComfortZone(true)
    setOnboardingIncomplete(false)
    setSectionsReloadKey((key) => key + 1)
  }

  const authHeaders = (token: string) => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })

  // Fetch saved event IDs
  useEffect(() => {
    if (!accessToken) { setSavedEventIds([]); return }
    fetch(`${env.NEXT_PUBLIC_API_URL}/events/saved`, {
      headers: authHeaders(accessToken),
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data: any[]) => setSavedEventIds(data.map((e) => e.id)))
      .catch(() => { })
  }, [accessToken])

  // Initial data fetch — each section loads independently for progressive rendering
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        setUser(user)

        const { data: sessionData } = await supabase.auth.getSession()
        const token = sessionData.session?.access_token || null
        setAccessToken(token)

        if (!token) {
          setInitialLoading(false)
          setLoadingRecs(false)
          setLoadingFollowed(false)
          setLoadingInterest(false)
          setLoadingComfortZone(false)
          setShowMatchAuthPrompt(true)
          return
        }

        // Fetch user data first (fast)
        const userRes = await fetch(`${env.NEXT_PUBLIC_API_URL}/user`, {
          headers: authHeaders(token),
        })
        const userData = await userRes.json()
        setUser((prev: any) => ({ ...prev, user_type: userData.user_type }))

        // #997 — the resolved scope, not the raw preference: the API decides
        // whether Local is honourable for this member.
        try {
          const scopeRes = await fetch(
            `${env.NEXT_PUBLIC_API_URL}/user/me/match-scope`,
            { headers: authHeaders(token) },
          )
          if (scopeRes.ok) setMatchScope(await scopeRes.json())
        } catch { }

        // Show the page layout immediately
        setInitialLoading(false)

          // Fire all three sections in parallel — each updates independently
          // Section 1: Recommendations (slowest — may hit LLM)
          ; void (async () => {
            try {
              const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/events/recommendations`, {
                headers: authHeaders(token),
              })
              // #1409 — the server's own onboarding gate (fail closed). Show
              // the finish-onboarding prompt in place of recommendations;
              // never fall through to the generic available-events fallback,
              // which would quietly show matches to a member who hasn't
              // earned them yet.
              if (res.status === 412) {
                setOnboardingIncomplete(true)
                setLoadingRecs(false)
                markSection('recs', false)
                return
              }
              if (res.ok) {
                const data = await res.json()
                const recs: Recommendation[] = (data.recommendations || []).map((rec: Recommendation) => ({
                  event: rec.event,
                  reasoning: rec.reasoning || 'This event aligns with your wellness journey and interests.',
                  relevanceScore: rec.relevanceScore,
                  fitBucket: rec.fitBucket,
                  matchedTopics: rec.matchedTopics,
                  image_url: getFirstImageUrl(rec.event.image_url || '', rec.event.categories),
                }))
                if (recs.length > 0) {
                  setRecommendations(recs)
                  setLoadingRecs(false)
                  markSection('recs', false)
                  return
                }
              }
            } catch { }
            // Fallback to available events (no reasoning — not personalised)
            try {
              const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/events/available`, {
                headers: authHeaders(token),
              })
              if (res.ok) {
                const available = await res.json()
                setRecommendations(available.slice(0, 6).map((event: RecommendationEvent): Recommendation => ({
                  event,
                  reasoning: 'This event is available in your area and matches your wellness interests.',
                  image_url: getFirstImageUrl(event.image_url || '', event.categories),
                })))
                markSection('recs', false)
              } else {
                markSection('recs', true)
              }
            } catch {
              markSection('recs', true)
            }
            setLoadingRecs(false)
          })()

          // Section 2: Followed facilitators events (fast)
          ; void (async () => {
            try {
              const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/events/followed-facilitators-events`, {
                headers: authHeaders(token),
              })
              if (res.ok) setFollowedEvents(await res.json())
              markSection('followed', !res.ok)
            } catch {
              markSection('followed', true)
            }
            setLoadingFollowed(false)
          })()

          // Section 3: Interest-based events (fast)
          ; void (async () => {
            try {
              const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/events/interest-based`, {
                headers: authHeaders(token),
              })
              if (res.ok) setInterestEvents(await res.json())
              markSection('interest', !res.ok)
            } catch {
              markSection('interest', true)
            }
            setLoadingInterest(false)
          })()

          // Section 4: Outside of Your Comfort Zone (fast)
          ; void (async () => {
            try {
              const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/events/outside-comfort-zone`, {
                headers: authHeaders(token),
              })
              if (res.ok) setComfortZoneEvents(await res.json())
              markSection('comfortZone', !res.ok)
            } catch {
              markSection('comfortZone', true)
            }
            setLoadingComfortZone(false)
          })()
      } catch (error) {
        console.error('Error fetching data:', error)
        setInitialLoading(false)
        setLoadingRecs(false)
        setLoadingFollowed(false)
        setLoadingInterest(false)
        setLoadingComfortZone(false)
        setFailedSections({ recs: true, followed: true, interest: true, comfortZone: true })
      }
    }
    void fetchData()
    // #997 — re-runs when the Local/Everywhere scope changes, so every section
    // comes back filtered by the new one.
  }, [sectionsReloadKey])

  // #997 — switch scope: persist, then refetch every section.
  const handleMatchScopeChange = async (next: MatchScope) => {
    if (!accessToken || !matchScope || next === matchScope.effective) return
    setMatchScopePending(true)
    try {
      const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/user/me/match-scope`, {
        method: 'PATCH',
        headers: authHeaders(accessToken),
        body: JSON.stringify({ match_scope: next }),
      })
      if (!res.ok) {
        setActionError("Couldn't change your match area. Please try again.")
        return
      }
      setActionError(null)
      setMatchScope({ ...matchScope, preference: next, effective: next })
      reloadSections()
    } catch {
      setActionError("Couldn't change your match area. Please try again.")
    } finally {
      setMatchScopePending(false)
    }
  }

  // Save/unsave toggle
  const handleSavedToggle = async (eventId: string, saved: boolean) => {
    if (!accessToken) return
    try {
      const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/events/${eventId}/save`, {
        method: saved ? 'post' : 'delete',
        headers: authHeaders(accessToken),
      })
      if (res.ok) {
        setSavedEventIds((prev) =>
          saved ? [...prev, eventId] : prev.filter((id) => id !== eventId)
        )
        setActionError(null)
      } else {
        setActionError("Couldn't save that event. Please try again.")
      }
    } catch {
      setActionError("Couldn't save that event. Please try again.")
    }
  }

  // Inline skeleton for a section that's still loading
  const SectionSkeleton = () => (
    <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex-shrink-0 w-[420px] bg-white rounded-xl shadow-sm border border-gray-100 animate-pulse">
          <div className="h-48 bg-gray-200 rounded-t-xl" />
          <div className="p-4 space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
            <div className="h-3 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      ))}
    </div>
  )

  if (initialLoading) {
    return (
      <div className="fixed inset-0 top-16 overflow-auto bg-[#F4ECE1]">
        <div className="min-h-full bg-[#F4ECE1] flex items-center justify-center">
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-40 h-40 border-4 border-[#F4ECE1] brightness-95 rounded-full"></div>
                <div className="w-40 h-40 border-4 border-[#DE866D] border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse text-[#DE866D]">
                  <PathCollectiveLogo variant="wordmark" className="!text-[#DE866D] text-3xl" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-[#2C2D2B]">
                Personalising your Path
              </h2>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                Matching you with experiences, communities and your people
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={150}>
      <AuthPromptDialog
        open={showMatchAuthPrompt}
        onOpenChange={setShowMatchAuthPrompt}
        title="Unlock your personalised matches"
        description="Create a free account so we can tailor wellness recommendations just for you."
        icon="✨"
      />
      <div className="fixed inset-0 top-16 overflow-auto bg-[#F4ECE1]">
        <main className="min-h-full bg-[#F4ECE1] px-6 py-10">
          <div className="max-w-7xl mx-auto space-y-10">
            {/* Header */}
            {/* #646 — the mobile header is `fixed`, so `w-full` resolves against
                the viewport (360px) while its static position already sits
                inside <main>'s px-6, pushing the "Play matching game" CTA 24px
                off the right edge. Match the content width instead, and let the
                row wrap so the CTA never squeezes the title. */}
            <div className="fixed  bg-[#F4ECE1]/70 backdrop-blur-md z-50  top-15 pt-2 pb-2 p-2 sm:p-0 sm:pt-0 sm:top-0 sm:relative flex flex-wrap items-center justify-between gap-2 mb-6 w-[calc(100%-3rem)] sm:w-full">
              <div className='p-3'>
                <h1 className="text-2xl  sm:text-3xl font-bold text-neutral-emphasis mb-1">My Matches</h1>
                <p className="text-neutral">Personalised recommendations tailored to your path</p>
              </div>
              <Link
                href="/match/game"
                className="mr-3 inline-flex items-center gap-2 rounded-full bg-[#DE866D] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#c9755e]"
              >
                <Sparkles className="h-4 w-4" />
                Play matching game
              </Link>
            </div>
            <div className="pt-31 sm:pt-0 max-w-7xl mx-auto space-y-10">

              {/* #997 — Local / Everywhere, above the first section so it reads
                  as applying to all of them (it does; the filter is server-side). */}
              {matchScope && (
                <MatchScopeToggle
                  scope={matchScope}
                  onChange={handleMatchScopeChange}
                  pending={matchScopePending}
                />
              )}

              <InlineError message={actionError} />

              {/* ═══ Section 1: Matches For You ═══ */}
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm  overflow-hidden p-4 sm:p-6">

                <SectionHeader
                  icon={Sparkles}
                  title="Suggested For You"
                  subtitle="Based on your profile and interests"
                  count={recommendations.length}
                  variant="teal"
                  actions={
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="w-full sm:w-auto">
                          <Button
                            size="sm"
                            className="flex items-center justify-center  gap-1.5 bg-white border border-[#DE866D] text-[#DE866D] hover:bg-[#DE866D]/10 transition text-xs h-8  w-full sm:w-auto"
                            onClick={() => setIsDeleteConfirmOpen(true)}
                          >
                            Update My Interests
                          </Button>
                        </div>

                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-800 text-white text-sm rounded-md px-3 py-2 shadow-md animate-fade-in">
                        Clear all data and restart your Path Collective experience.
                      </TooltipContent>
                    </Tooltip>
                  }
                />
                <div className="p-5">
                  {loadingRecs ? (
                    <SectionSkeleton />
                  ) : onboardingIncomplete ? (
                    // #1409 — hidden until onboarding is COMPLETE (server-
                    // enforced: the 412 above), with a way straight back in.
                    <EmptyState
                      message="Finish onboarding to unlock matches personalised to you."
                      cta={{ label: 'Finish onboarding', href: '/onboarding' }}
                    />
                  ) : failedSections.recs ? (
                    <LoadError message="We couldn't load your suggestions." onRetry={reloadSections} />
                  ) : recommendations.length > 0 ? (
                    <div className="flex gap-2 sm:gap-4 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                      {recommendations.map(({ event, reasoning, relevanceScore, fitBucket, image_url }) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          reasoning={reasoning}
                          relevanceScore={relevanceScore}
                          fitBucket={fitBucket}
                          imageUrl={image_url}
                          savedEventIds={savedEventIds}
                          onSavedToggle={handleSavedToggle}
                        />
                      ))}
                    </div>
                  ) : (
                    <EmptyState message="No personalised matches yet. Complete your onboarding to get recommendations." />
                  )}
                </div>
              </section>

              {/* ═══ Section 2: Events From Facilitators You Follow ═══ */}
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader
                  icon={Users}
                  title="From Your Community"
                  subtitle="Upcoming experiences from facilitators you follow"
                  count={followedEvents.length}
                  variant="coral"
                />
                <div className="p-5">
                  {loadingFollowed ? (
                    <SectionSkeleton />
                  ) : failedSections.followed ? (
                    <LoadError message="We couldn't load events from facilitators you follow." onRetry={reloadSections} />
                  ) : followedEvents.length > 0 ? (
                    <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
                      {followedEvents.map((event: any) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          savedEventIds={savedEventIds}
                          onSavedToggle={handleSavedToggle}
                        />
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      message="No upcoming events from facilitators you follow."
                      cta={{ label: "Discover facilitators", href: "/facilitators" }}
                    />
                  )}
                </div>
              </section>

              {/* ═══ Section 3: Because You Showed Interest ═══ */}
              {(loadingInterest || failedSections.interest || interestEvents.length > 0) && (
                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <SectionHeader
                    icon={Eye}
                    title="Because You Showed Interest"
                    subtitle="Based on events you've viewed, saved, and shared"
                    count={interestEvents.length}
                    variant="indigo"
                  />
                  <div className="p-5">
                    {loadingInterest ? (
                      <SectionSkeleton />
                    ) : failedSections.interest ? (
                      <LoadError message="We couldn't load these events." onRetry={reloadSections} />
                    ) : (
                      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
                        {interestEvents.map((event: any) => (
                          <EventCard
                            key={event.id}
                            event={event}
                            savedEventIds={savedEventIds}
                            onSavedToggle={handleSavedToggle}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* ═══ Section 4: Outside of Your Comfort Zone ═══ */}
              {/* #999 — always rendered. The API now relaxes its filters before
                  it returns nothing, but when it still does, the section says so
                  instead of vanishing and reading as a deleted feature. */}
              <section
                data-testid="comfort-zone-section"
                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
              >
                <SectionHeader
                  icon={Compass}
                  title="Outside of Your Comfort Zone"
                  subtitle="Try something new — events you haven't explored before"
                  count={comfortZoneEvents.length}
                  variant="mint"
                />
                <div className="p-5">
                  {loadingComfortZone ? (
                    <SectionSkeleton />
                  ) : failedSections.comfortZone ? (
                    <LoadError message="We couldn't load these events." onRetry={reloadSections} />
                  ) : comfortZoneEvents.length === 0 ? (
                    <p
                      data-testid="comfort-zone-empty"
                      className="text-sm text-gray-500"
                    >
                      Nothing new near you this week —{' '}
                      <Link
                        href="/discovery"
                        className="font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900"
                      >
                        explore Discover
                      </Link>
                      .
                    </p>
                  ) : (
                    <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
                      {comfortZoneEvents.map((event: any) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          savedEventIds={savedEventIds}
                          onSavedToggle={handleSavedToggle}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </section>

            </div>
          </div>

          {/* Delete Data Confirmation Modal */}
          {isDeleteConfirmOpen && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-lg w-[90%]  sm:w-full  max-w-md sm:p-6 p-3 relative">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xl sm:text-2xl">&#9888;&#65039;</span>
                  <h2 className="text-l sm:text-xl  font-semibold text-[#2C2D2B]">Start Onboarding again</h2>
                </div>
                <p className=" text-xs sm:text-sm text-gray-600 mb-3 sm:mb-6 leading-relaxed">
                  You&apos;re about to reset your onboarding journey. This removes your answers and
                  selected wellness sphere, but keeps your basic profile details, like age, gender,
                  and location. You&apos;ll be guided through onboarding again to refresh your
                  matches.
                </p>
                <div className="flex justify-start sm:justify-end gap-3">
                  <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} className="px-4 py-2">
                    Go back
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        const { data: sessionData } = await supabase.auth.getSession()
                        const token = sessionData.session?.access_token
                        if (!token) {
                          window.location.href = '/login'
                          return
                        }
                        const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/user/data/self`, {
                          method: 'DELETE',
                          headers: { Authorization: `Bearer ${token}` },
                        })
                        if (response.ok) {
                          localStorage.removeItem('onboarding_complete')
                          window.location.href = '/onboarding'
                        } else {
                          const data = await response.json().catch(() => ({}))
                          console.error('Reset onboarding failed:', response.status, data)
                          alert(data.message || 'Failed to reset onboarding. Please try again.')
                          setIsDeleteConfirmOpen(false)
                        }
                      } catch (err) {
                        console.error('Reset onboarding error:', err)
                        alert('Something went wrong. Please try again.')
                        setIsDeleteConfirmOpen(false)
                      }
                    }}
                    variant="outline"
                    className="bg-[#FCEEE8] text-[#DE866D] hover:bg-[#DE866D]/10 border-[#DE866D]/20"
                  >
                    Restart Onboarding
                  </Button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </TooltipProvider>
  )
}
